import React, { useEffect, useState } from "react";
import { IResourceComponentsProps } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { Column, ColumnDef, flexRender } from "@tanstack/react-table";
import {
  ScrollArea,
  Table,
  Pagination,
  Flex,
  Select,
  LoadingOverlay,
  Text,
  Tooltip,
  Box,
  TextInput,
  TextInputProps,
  Paper,
  Group,
  ActionIcon,
  Collapse,
  Badge,
  UnstyledButton,
  Button,
  Menu,
} from "@mantine/core";
import { useDelete } from "@refinedev/core";
import {
  List,
  CreateButton,
  DateField,
} from "@refinedev/mantine";
import {
  IconFilter,
  IconFilterOff,
  IconChevronUp,
  IconChevronDown,
  IconX,
  IconDots,
  IconEye,
  IconTrash,
  IconUpload,
  IconDownload,
  IconFileSpreadsheet,
} from "@tabler/icons";
import { useNavigate } from "react-router-dom";
import useReviewRequestBlacklistPermissions from "./useReviewRequestBlacklistPermissions";
import {
  ReviewRequestBlacklist,
  REASON_OPTIONS,
  REASON_COLORS,
  REASON_LABELS,
} from "entities/review_request_blacklist";
import useExportCsv from "./useExportCsv";
import { ImportCsvModal } from "./ImportCsvModal";

/**
 * TextInput that debounces writes to column.setFilterValue
 * but reads from column.getFilterValue to stay synced with the query string.
 */
function DebouncedColumnInput({
  column,
  delay = 500,
  ...inputProps
}: {
  column: Column<ReviewRequestBlacklist, unknown>;
  delay?: number;
} & Omit<TextInputProps, "value" | "onChange">) {
  const columnValue = (column.getFilterValue() as string) ?? "";
  const [localValue, setLocalValue] = useState(columnValue);

  useEffect(() => {
    setLocalValue(columnValue);
  }, [columnValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      column.setFilterValue(localValue || undefined);
    }, delay);
    return () => clearTimeout(timeout);
  }, [localValue, delay, column]);

  return (
    <TextInput
      value={localValue}
      onChange={(e) => setLocalValue(e.currentTarget.value)}
      {...inputProps}
    />
  );
}

/** Row action menu with view and delete options. */
function RowActions({
  id,
  canDelete,
}: {
  id: string | number;
  canDelete: boolean;
}) {
  const navigate = useNavigate();
  const { mutate: deleteOne } = useDelete();

  return (
    <Menu shadow="md" width={160} position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          radius="xl"
        >
          <IconDots size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          icon={<IconEye size={14} />}
          onClick={() => navigate(`/review_request_blacklists/show/${id}`)}
        >
          View details
        </Menu.Item>
        {canDelete && (
          <>
            <Menu.Divider />
            <Menu.Item
              icon={<IconTrash size={14} />}
              color="red"
              onClick={() =>
                deleteOne({
                  resource: "review_request_blacklists",
                  id,
                })
              }
            >
              Delete
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

export const ReviewRequestBlacklistList: React.FC<
  IResourceComponentsProps
> = () => {
  const { canCreate, canDelete } = useReviewRequestBlacklistPermissions();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const columns = React.useMemo<ColumnDef<ReviewRequestBlacklist>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
        size: 80,
        enableColumnFilter: false,
      },
      {
        id: "email",
        accessorKey: "email",
        header: "Email",
        cell: function render({ getValue }) {
          return (
            <Text size="sm" weight={500}>
              {getValue<string>()}
            </Text>
          );
        },
        meta: {
          filterOperator: "contains",
        },
      },
      {
        id: "reason",
        accessorKey: "reason",
        header: "Reason",
        cell: ({ getValue }) => {
          const reason = getValue<string>();
          return (
            <Badge
              variant="filled"
              color={REASON_COLORS[reason] || "gray"}
              size="sm"
              radius="sm"
            >
              {REASON_LABELS[reason] || reason}
            </Badge>
          );
        },
      },
      {
        id: "source_detail",
        accessorKey: "source_detail",
        header: "Source Detail",
        cell: function render({ getValue }) {
          const value = getValue<string>();
          if (!value) {
            return (
              <Text size="sm" color="dimmed" italic>
                —
              </Text>
            );
          }
          return (
            <Tooltip label={value} multiline maw={300} withinPortal>
              <Text size="sm" lineClamp={1} maw={280}>
                {value}
              </Text>
            </Tooltip>
          );
        },
        meta: {
          filterOperator: "contains",
        },
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created At",
        enableColumnFilter: false,
        cell: function render({ getValue }) {
          return (
            <DateField value={getValue<any>()} format="YYYY-MM-DD HH:mm" />
          );
        },
      },
      {
        id: "actions",
        header: "",
        size: 50,
        cell: function render({ row: { original: blacklist } }) {
          return (
            <RowActions
              id={blacklist.id}
              canDelete={canDelete}
            />
          );
        },
        meta: {
          isPlaceholder: true,
        },
      },
    ],
    [canCreate, canDelete]
  );

  const {
    getHeaderGroups,
    getRowModel,
    setOptions,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { data: tableData, isFetching },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      pagination: {
        pageSize: 50,
      },
    },
  });

  setOptions((prev) => ({
    ...prev,
    meta: {
      ...prev.meta,
    },
  }));

  const headers = getFlatHeaders();
  const { exportCsv, isExporting } = useExportCsv(headers);

  const emailColumn = headers.find((h) => h.id === "email")?.column;
  const reasonColumn = headers.find((h) => h.id === "reason")?.column;
  const sourceDetailColumn = headers.find(
    (h) => h.id === "source_detail"
  )?.column;

  const appliedFilters = headers.filter((h) => h.column.getIsFiltered());
  const hasActiveFilters = appliedFilters.length > 0;

  const reasonValue = (reasonColumn?.getFilterValue() as string) ?? null;

  const clearAllFilters = () => {
    appliedFilters.forEach((h) => h.column.setFilterValue(undefined));
  };

  return (
    <List
      canCreate={canCreate}
      headerButtons={
        <>
          <CreateButton />
          <Button
            size="xs"
            variant="default"
            leftIcon={<IconUpload size={16} />}
            onClick={() => setImportModalOpen(true)}
          >
            Import CSV
          </Button>
          <Button
            size="xs"
            variant="default"
            leftIcon={<IconDownload size={16} />}
            onClick={exportCsv}
            loading={isExporting}
          >
            Export CSV
          </Button>
          <Button
            size="xs"
            variant="subtle"
            leftIcon={<IconFileSpreadsheet size={16} />}
            component="a"
            href="/assets/example_blacklist.csv"
            download
          >
            Example CSV
          </Button>
        </>
      }
    >
      <ImportCsvModal
        opened={importModalOpen}
        onClose={() => setImportModalOpen(false)}
      />
      {/* Filter bar */}
      <Paper
        p="xs"
        mb="md"
        sx={(theme) => ({
          backgroundColor:
            theme.colorScheme === "dark"
              ? theme.colors.dark[6]
              : theme.colors.gray[0],
          borderRadius: theme.radius.sm,
        })}
      >
        <Group position="apart" mb={filtersOpen ? "sm" : 0}>
          <UnstyledButton onClick={() => setFiltersOpen((o) => !o)}>
            <Group spacing="xs">
              <IconFilter size={16} />
              <Text size="sm" weight={500}>
                Filters
              </Text>
              {hasActiveFilters && (
                <Badge size="xs" variant="filled" color="cyan" radius="xl">
                  {appliedFilters.length}
                </Badge>
              )}
              {filtersOpen ? (
                <IconChevronUp size={14} />
              ) : (
                <IconChevronDown size={14} />
              )}
            </Group>
          </UnstyledButton>

          {hasActiveFilters && (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={clearAllFilters}
              title="Clear all filters"
            >
              <IconFilterOff size={14} />
            </ActionIcon>
          )}
        </Group>

        <Collapse in={filtersOpen}>
          <Flex gap="md" wrap="wrap" align="flex-end">
            {emailColumn && (
              <DebouncedColumnInput
                column={emailColumn}
                label="Email"
                placeholder="Search by email..."
                size="xs"
                sx={{ flex: "1 1 200px", maxWidth: 280 }}
              />
            )}
            <Select
              label="Reason"
              placeholder="All reasons"
              data={REASON_OPTIONS}
              value={reasonValue}
              onChange={(value) =>
                reasonColumn?.setFilterValue(value || undefined)
              }
              clearable
              size="xs"
              sx={{ flex: "1 1 160px", maxWidth: 200 }}
            />
            {sourceDetailColumn && (
              <DebouncedColumnInput
                column={sourceDetailColumn}
                label="Source Detail"
                placeholder="Search source..."
                size="xs"
                sx={{ flex: "1 1 200px", maxWidth: 280 }}
              />
            )}
          </Flex>
        </Collapse>

        {/* Applied filter chips */}
        {hasActiveFilters && !filtersOpen && (
          <Flex gap={6} wrap="wrap" mt="xs">
            {appliedFilters.map((header) => {
              const filterValue = header.column.getFilterValue();
              let displayValue: string;

              if (header.id === "reason") {
                displayValue =
                  REASON_LABELS[filterValue as string] ||
                  (filterValue as string);
              } else {
                displayValue = String(filterValue);
              }

              return (
                <Badge
                  key={header.id}
                  variant="outline"
                  color="cyan"
                  size="sm"
                  pr={3}
                  rightSection={
                    <ActionIcon
                      size={14}
                      color="cyan"
                      radius="xl"
                      variant="transparent"
                      onClick={() =>
                        header.column.setFilterValue(undefined)
                      }
                    >
                      <IconX size={10} />
                    </ActionIcon>
                  }
                >
                  {header.column.columnDef.header as string}: {displayValue}
                </Badge>
              );
            })}
          </Flex>
        )}
      </Paper>

      {/* Table */}
      <ScrollArea>
        <Box pos="relative" sx={{ minWidth: "100%" }}>
          <LoadingOverlay visible={isFetching} />
          <Table
            highlightOnHover
            verticalSpacing="sm"
            horizontalSpacing="md"
            sx={{ tableLayout: "auto", width: "100%" }}
          >
            <thead>
              {getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        whiteSpace: "nowrap",
                        cursor: header.column.getCanSort()
                          ? "pointer"
                          : "default",
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {!header.isPlaceholder &&
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      {header.column.getIsSorted() === "asc" && " ↑"}
                      {header.column.getIsSorted() === "desc" && " ↓"}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {getRowModel().rows.length === 0 && !isFetching && (
                <tr>
                  <td colSpan={columns.length}>
                    <Text
                      py="xl"
                      align="center"
                      color="dimmed"
                      size="sm"
                    >
                      No blacklist entries found
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Box>
      </ScrollArea>

      <Flex justify="flex-end" mt="md">
        <Pagination
          total={pageCount}
          page={current}
          onChange={setCurrent}
        />
      </Flex>
    </List>
  );
};
